import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-hot-toast';
import PageBackButton from '../../components/PageBackButton';
import { parentMeetingsAPI } from '../../services/api';
import { formatCalendarDate } from '../../utils/dateTimeUtils';
import { INDIA_TZ, parentLine, previewClocks, studentLabel, suggestTitle } from '../../utils/parentMeetingTime';
import {
  buildMeetingListParams,
  olderArchiveToYmd,
  RECENT_MEETING_DAYS,
  unwrapMeetingsResponse,
} from '../Meetings/meetingListUtils';

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const emptyForm = {
  title: '',
  autoTitle: '',
  meetingDate: '',
  startTime: '',
  endTime: '',
  joinUrl: '',
  agenda: '',
  notes: '',
  student1: null,
  batch1: '',
  student2: null,
  batch2: '',
};

function batchOf(student, batchId) {
  return (student?.batches || []).find((batch) => String(batch.id) === String(batchId)) || null;
}

function snapshotStudent(slot) {
  if (!slot) return null;
  return {
    id: slot.studentId,
    name: slot.name,
    displayId: slot.displayId || '',
    country: slot.country || '',
    timezone: slot.timezone || '',
    fatherName: '',
    motherName: '',
    phone: '',
    batches: slot.batchId
      ? [{ id: slot.batchId, name: slot.batchName || 'Batch', teacherName: slot.teacherName || '', country: '' }]
      : [],
  };
}

function chosenBatch(student, savedBatchId) {
  if (!student) return '';
  if ((student.batches || []).some((batch) => String(batch.id) === String(savedBatchId))) return savedBatchId;
  if (student.batches.length === 1) return student.batches[0].id;
  return '';
}

function studentSummary(student) {
  const teacher = student.teacherName ? `Teacher ${student.teacherName}` : 'No teacher on this batch';
  return [student.name, student.batchName, teacher, student.country].filter(Boolean).join(' · ');
}

function BatchField({ student, value, onChange }) {
  if (!student) return null;
  if (!student.batches.length) {
    return (
      <Typography color="error" variant="body2">
        {student.name} has no active batch, so this meeting cannot include them.
      </Typography>
    );
  }
  const selected = batchOf(student, value);
  return (
    <Stack spacing={0.5}>
      <TextField
        select
        label="Batch"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        required
        fullWidth
        disabled={student.batches.length <= 1}
        helperText={student.batches.length > 1 ? 'This student is in more than one batch' : 'Active batch'}
      >
        {student.batches.map((batch) => (
          <MenuItem key={batch.id} value={batch.id}>
            {batch.name}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="body2" color="text.secondary">
        Teacher: {selected?.teacherName || 'No teacher on this batch'}
      </Typography>
    </Stack>
  );
}

export default function ParentMeetings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [studentQuery, setStudentQuery] = useState('');
  const [range, setRange] = useState('recent');
  const [search, setSearch] = useState('');
  const [archiveFrom, setArchiveFrom] = useState('');
  const [archiveTo, setArchiveTo] = useState(olderArchiveToYmd());
  const [page, setPage] = useState(1);
  const searchDebounced = useDebouncedValue(search);
  const studentDebounced = useDebouncedValue(studentQuery);

  const listParams = useMemo(
    () => buildMeetingListParams({ range, search: searchDebounced, archiveFrom, archiveTo, page }),
    [range, searchDebounced, archiveFrom, archiveTo, page]
  );

  const meetingsQuery = useQuery(['parent-meetings', listParams], () =>
    parentMeetingsAPI.list(listParams).then((res) => unwrapMeetingsResponse(res))
  );
  const studentSearch = useQuery(
    ['parent-meeting-students', studentDebounced],
    () => parentMeetingsAPI.searchStudents({ q: studentDebounced }).then((res) => res.data),
    { enabled: showForm, keepPreviousData: true }
  );
  const siblingsQuery = useQuery(
    ['parent-meeting-siblings', form.student1?.id],
    () => parentMeetingsAPI.siblings(form.student1.id).then((res) => res.data),
    { enabled: showForm && Boolean(form.student1?.id) }
  );

  const meetings = meetingsQuery.data?.rows || [];
  const pagination = meetingsQuery.data?.pagination;
  const studentOptions = studentSearch.data?.data || [];
  const siblingOptions = (siblingsQuery.data?.data || []).filter(
    (student) => String(student.id) !== String(form.student1?.id)
  );

  const saveMutation = useMutation(
    (payload) => (editingId ? parentMeetingsAPI.update(editingId, payload) : parentMeetingsAPI.create(payload)),
    {
      onSuccess: () => {
        toast.success(editingId ? 'Parent meeting updated' : 'Parent meeting scheduled');
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(false);
        setStudentQuery('');
        queryClient.invalidateQueries(['parent-meetings']);
        queryClient.invalidateQueries(['parent-meeting-calendar']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save the parent meeting'),
    }
  );

  const deleteMutation = useMutation((id) => parentMeetingsAPI.remove(id), {
    onSuccess: () => {
      toast.success('Parent meeting cancelled');
      queryClient.invalidateQueries(['parent-meetings']);
      queryClient.invalidateQueries(['parent-meeting-calendar']);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not cancel the parent meeting'),
  });

  const clocks = previewClocks({
    meetingDate: form.meetingDate,
    startTime: form.startTime,
    endTime: form.endTime,
    students: [form.student1, form.student2],
  });

  const applyStudents = (next) => {
    const autoTitle = suggestTitle([next.student1, next.student2]);
    const title = !next.title || next.title === next.autoTitle ? autoTitle : next.title;
    setForm({ ...next, title, autoTitle });
  };

  const chooseStudent = (slot, student) => {
    if (slot === 1) {
      applyStudents({
        ...form,
        student1: student,
        batch1: student?.batches?.length === 1 ? student.batches[0].id : '',
        student2: null,
        batch2: '',
      });
      return;
    }
    applyStudents({
      ...form,
      student2: student,
      batch2: student?.batches?.length === 1 ? student.batches[0].id : '',
    });
  };

  const startEdit = async (meeting) => {
    const load = async (slot) => {
      if (!slot) return null;
      try {
        const res = await parentMeetingsAPI.getStudent(slot.studentId);
        return res.data?.data || snapshotStudent(slot);
      } catch {
        return snapshotStudent(slot);
      }
    };
    const [student1, student2] = await Promise.all([
      load(meeting.students?.[0]),
      load(meeting.students?.[1]),
    ]);
    const next = {
      ...emptyForm,
      title: meeting.title || '',
      meetingDate: meeting.meetingDate || '',
      startTime: meeting.startTime || '',
      endTime: meeting.endTime || '',
      joinUrl: meeting.joinUrl || '',
      agenda: meeting.agenda || '',
      notes: meeting.notes || '',
      student1,
      batch1: chosenBatch(student1, meeting.students?.[0]?.batchId),
      student2,
      batch2: chosenBatch(student2, meeting.students?.[1]?.batchId),
    };
    next.autoTitle = suggestTitle([student1, student2]);
    setEditingId(meeting.id);
    setForm(next);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!form.student1 || !form.batch1) {
      toast.error('Choose a student and batch');
      return;
    }
    if (form.student2 && !form.batch2) {
      toast.error('Choose a batch for the second student');
      return;
    }
    const students = [{ studentId: form.student1.id, batchId: form.batch1 }];
    if (form.student2) students.push({ studentId: form.student2.id, batchId: form.batch2 });
    saveMutation.mutate({
      title: form.title.trim(),
      meetingDate: form.meetingDate,
      startTime: form.startTime,
      endTime: form.endTime,
      joinUrl: form.joinUrl.trim(),
      agenda: form.agenda,
      notes: form.notes,
      students,
    });
  };

  const searching = Boolean(String(search || '').trim());
  const archiveCutoff = archiveTo || olderArchiveToYmd();
  const caption = searching
    ? 'Searching all parent meetings'
    : range === 'older'
      ? `Meetings on or before ${formatCalendarDate(archiveCutoff)}`
      : `Upcoming and the last ${RECENT_MEETING_DAYS} days`;

  return (
    <Box>
      <PageBackButton />
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5">Parent meetings</Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule a parent meeting for an assigned student who has an active batch. The batch teacher is shown for reference. Enter the time in India ({INDIA_TZ}); the parent’s local time is shown beside it. The student’s RM gets a CRM notification about 24 hours before the India start time.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate('/rm/parent-meetings/calendar')}>Calendar</Button>
          {!showForm ? (
            <Button variant="contained" onClick={() => setShowForm(true)}>
              New meeting
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Collapse in={showForm}>
        <Paper component="form" onSubmit={submit} sx={{ p: 2, mb: 3, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {editingId ? 'Edit parent meeting' : 'New parent meeting'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={studentOptions}
                value={form.student1}
                onChange={(_event, value) => chooseStudent(1, value)}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input' || reason === 'clear') setStudentQuery(value);
                }}
                filterOptions={(options) => options}
                getOptionLabel={studentLabel}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                loading={studentSearch.isFetching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Student"
                    required
                    helperText={
                      studentSearch.data?.truncated
                        ? 'Showing the first 25. Keep typing to search all assigned students.'
                        : 'Search name, student id, parent, phone, batch, or country'
                    }
                  />
                )}
              />
              {form.student1 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[parentLine(form.student1), form.student1.country, form.student1.timezone].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <BatchField
                student={form.student1}
                value={form.batch1}
                onChange={(batch1) => setForm({ ...form, batch1 })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={siblingOptions}
                value={form.student2}
                disabled={!form.student1}
                onChange={(_event, value) => chooseStudent(2, value)}
                getOptionLabel={studentLabel}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                loading={siblingsQuery.isFetching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Second student (optional)"
                    helperText={
                      siblingsQuery.data?.message
                      || 'Only a student with the same father name, mother name, and phone number can be added.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <BatchField
                student={form.student2}
                value={form.batch2}
                onChange={(batch2) => setForm({ ...form, batch2 })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.meetingDate}
                onChange={(event) => setForm({ ...form, meetingDate: event.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Start (India)"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.startTime}
                onChange={(event) => setForm({ ...form, startTime: event.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="End (India)"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.endTime}
                onChange={(event) => setForm({ ...form, endTime: event.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">India: {clocks?.india || 'Enter a date and time'}</Typography>
                {(clocks?.parents || []).map((parent) => (
                  <Typography key={`${parent.timezone}-${parent.names.join('-')}`} variant="body2">
                    Parent ({parent.names.filter(Boolean).join(', ')}): {parent.label}
                  </Typography>
                ))}
                {!clocks ? (
                  <Typography variant="caption" color="text.secondary">
                    Both clocks include the timezone. The 24-hour reminder uses India time.
                  </Typography>
                ) : null}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Join link"
                value={form.joinUrl}
                onChange={(event) => setForm({ ...form, joinUrl: event.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Agenda (optional)"
                value={form.agenda}
                onChange={(event) => setForm({ ...form, agenda: event.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Notes (optional)"
                value={form.notes}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saveMutation.isLoading}>
              {editingId ? 'Save changes' : 'Schedule meeting'}
            </Button>
            <Button
              onClick={() => {
                setForm(emptyForm);
                setEditingId(null);
                setShowForm(false);
                setStudentQuery('');
              }}
            >
              Close
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search title, student, parent, batch, teacher, or country"
          fullWidth
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            label="Recent"
            color={range === 'recent' ? 'primary' : 'default'}
            variant={range === 'recent' ? 'filled' : 'outlined'}
            onClick={() => {
              setRange('recent');
              setPage(1);
            }}
          />
          <Chip
            label="Older"
            color={range === 'older' ? 'primary' : 'default'}
            variant={range === 'older' ? 'filled' : 'outlined'}
            onClick={() => {
              setRange('older');
              setPage(1);
            }}
          />
          {range === 'older' ? (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={archiveFrom}
                onChange={(event) => {
                  setArchiveFrom(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={archiveTo || archiveCutoff}
                onChange={(event) => {
                  setArchiveTo(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 160 }}
              />
            </>
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {caption}
          {pagination?.total ? ` · ${pagination.total} meeting${pagination.total === 1 ? '' : 's'}` : ''}
        </Typography>
      </Stack>

      {meetingsQuery.isLoading ? <CircularProgress /> : null}
      {!meetingsQuery.isLoading && !meetings.length ? (
        <Typography color="text.secondary">No parent meetings in this view.</Typography>
      ) : null}
      <Stack spacing={1}>
        {meetings.map((meeting) => (
          <Accordion key={meeting.id} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
                sx={{ width: '100%', pr: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{meeting.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCalendarDate(meeting.meetingDate)} · {(meeting.students || []).map(studentSummary).join(' | ')}
                  </Typography>
                  <Typography variant="body2">India: {meeting.clocks?.india?.label}</Typography>
                  {(meeting.clocks?.parents || []).map((parent) => (
                    <Typography key={`${meeting.id}-${parent.timezone}`} variant="body2">
                      Parent ({(parent.names || []).join(', ')}): {parent.label}
                    </Typography>
                  ))}
                </Box>
                <Stack direction="row" spacing={1} onClick={(event) => event.stopPropagation()}>
                  {meeting.joinUrl ? (
                    <Button size="small" href={meeting.joinUrl} target="_blank" rel="noreferrer">Join</Button>
                  ) : null}
                  <Button size="small" onClick={() => startEdit(meeting)}>Edit</Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      if (window.confirm('Cancel this parent meeting?')) deleteMutation.mutate(meeting.id);
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {meeting.rmName ? <Typography variant="body2" sx={{ mb: 1 }}>RM: {meeting.rmName}</Typography> : null}
              {meeting.agenda ? <Typography variant="body2" sx={{ mb: 1 }}><strong>Agenda:</strong> {meeting.agenda}</Typography> : null}
              {meeting.notes ? <Typography variant="body2"><strong>Notes:</strong> {meeting.notes}</Typography> : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
      {pagination?.pages > 1 ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pagination.pages}
            page={pagination.page || page}
            onChange={(_event, next) => setPage(next)}
          />
        </Stack>
      ) : null}
    </Box>
  );
}
